import { useState } from "react";
import { Stack, Typography, Alert } from "@mui/material";
import { Button, FoodsIllustrations, WhiteBox } from "@foodpilot/foods";
import { useGetPostFeed, useDeletePost } from "../../../../api/client-api/social/usePosts";
import { useToggleReaction } from "../../../../api/client-api/social/usePostReactions";
import { PostCard } from "../PostCard/PostCard";
import { FeedSkeleton } from "./FeedSkeleton";
import { InfinitePostList } from "./InfinitePostList";
import { ReactionType } from "../../types/reaction.types";
import { Post } from "../../types/post.types";
import { ExpandedEditPost } from "../EditPost/ExpandedEditPost";
import { useUserContext } from "../../../../context/User/useUserContext";
import { useTranslation } from "react-i18next";
import { Topic } from "../../types/post.types";
import { matchesPublicationCategory, PublicationCategory } from "../../utils/postType";
import { useSnackbar } from "src/utils/useSnackbar";

interface PostFeedProps {
    feedType: "timeline" | "trending" | "following";
    topicFilter?: number;
    categoryFilter?: PublicationCategory;
    onTopicClick?: (topic: Topic) => void;
    onCreatePost?: () => void;
}

export const PostFeed = ({ feedType, topicFilter, categoryFilter, onTopicClick, onCreatePost }: PostFeedProps) => {
    const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetPostFeed(
        feedType,
        topicFilter,
    );
    const { user } = useUserContext();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const [editingPost, setEditingPost] = useState<Post | null>(null);

    const toggleReaction = useToggleReaction();
    const deletePost = useDeletePost();

    const handleReact = (postId: number, type: ReactionType) => {
        toggleReaction.mutate({ postId, type });
    };

    const handleShare = async (postId: number) => {
        const url = `${window.location.origin}/social#social-post-${postId}`;

        try {
            await navigator.clipboard.writeText(url);
            setSnackbarProps({
                type: "success",
                message: t("social.share.copied", "Post link copied to clipboard."),
            });
        } catch (error) {
            setSnackbarProps({
                type: "error",
                message: t("social.share.copy_failed", "Unable to copy the post link."),
            });
        }
    };

    const handleEdit = (post: Post) => {
        setEditingPost(post);
    };

    const handleEditCancel = () => {
        setEditingPost(null);
    };

    const handleEditSuccess = () => {
        setEditingPost(null);
    };

    const handleDelete = (postId: number) => {
        deletePost.mutate(postId);
    };

    if (isLoading) {
        return <FeedSkeleton />;
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {(error as any)?.message || t("social.feed.error.load", "Failed to load posts. Please try again.")}
            </Alert>
        );
    }

    if (!data?.pages[0]?.posts?.length) {
        return (
            <WhiteBox sx={{ p: 3 }}>
                <Stack alignItems="center" justifyContent="center" gap={2} textAlign="center">
                    <FoodsIllustrations illustration="communities" width={112} height={112} />
                    <Stack gap={0.5}>
                        <Typography variant="h6" color="text.secondary">
                            {t("social.feed.empty_title", "Start the conversation")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {feedType === "timeline" &&
                                t("social.feed.empty_timeline", "Be the first to share something with your team.")}
                            {feedType === "trending" &&
                                t("social.feed.empty_trending", "No trending posts at the moment.")}
                            {feedType === "following" &&
                                t("social.feed.empty_following", "Start following topics to see posts here.")}
                        </Typography>
                    </Stack>
                    <Button variant="primary" onClick={onCreatePost}>
                        {t("social.feed.empty_cta", "Create your first post")}
                    </Button>
                </Stack>
            </WhiteBox>
        );
    }

    const posts = data.pages.flatMap((page) => page.posts);
    const filteredPosts =
        categoryFilter ?
            posts.filter((post) => matchesPublicationCategory(post.content, post.topics ?? [], categoryFilter))
        :   posts;

    if (filteredPosts.length === 0 && posts.length > 0) {
        return (
            <WhiteBox sx={{ p: 3 }}>
                <Stack alignItems="center" justifyContent="center" gap={2} textAlign="center">
                    <FoodsIllustrations illustration="informations" width={80} height={80} />
                    <Typography variant="body1" color="text.secondary">
                        {t("social.feed.empty_category", "No posts match this category yet.")}
                    </Typography>
                    <Button variant="primary" onClick={onCreatePost}>
                        {t("social.feed.empty_cta", "Create your first post")}
                    </Button>
                </Stack>
            </WhiteBox>
        );
    }

    return (
        <>
            <InfinitePostList
                posts={filteredPosts}
                onLoadMore={fetchNextPage}
                hasMore={hasNextPage}
                isLoading={isFetchingNextPage}
                renderPost={(post) => (
                    <PostCard
                        key={post.id}
                        post={post}
                        onReact={handleReact}
                        onShare={handleShare}
                        onTopicClick={onTopicClick}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                )}
            />

            {/* Edit Post Modal */}
            {editingPost && (
                <ExpandedEditPost
                    post={editingPost}
                    open={Boolean(editingPost)}
                    onClose={handleEditCancel}
                    user={user}
                    onSuccess={handleEditSuccess}
                />
            )}
        </>
    );
};
